import { cn } from '@ais-ui/utils';
import { Checkbox } from '@ais-ui/ui-core';
import { LayerItem, useLayers, baseColours } from '@ais-ui/react-openlayers';
import { TreeGuide } from './TreeGuide';
import { ColorSwatch } from './ColorSwatch';
import { LayerRow } from '../LayerRow';
import { getIconFromLayerType } from '@ais-ui/react-openlayers';
import { useMemo } from 'react';

interface ChildLayerRowProps {
  layer: LayerItem;
  isFirst: boolean;
  isLast: boolean;
  onFlyTo: (layer: LayerItem) => void;
}

/**
 * A child layer row within a nested/grouped layer.
 * Shows tree guide lines, checkbox, color swatch, name, and metadata.
 * Clicking the row (not checkbox/swatch) triggers fly-to.
 */
export const ChildLayerRow = ({
  layer,
  isFirst,
  isLast,
  onFlyTo,
}: ChildLayerRowProps) => {
  const { toggleLayerVisibility, changeColor } = useLayers();

  const LayerIcon = useMemo(
    () => getIconFromLayerType(layer.type),
    [layer.type],
  );

  const displayColor = useMemo(() => {
    const colorKey = layer.properties?.color as
      | keyof typeof baseColours
      | undefined;
    return (
      (colorKey && baseColours[colorKey]) ||
      layer.color ||
      layer.properties?.defaultColor ||
      baseColours.blue
    );
  }, [layer.color, layer.properties]);

  const handleColorChange = (colorName: keyof typeof baseColours) => {
    changeColor(layer, colorName);
  };

  return (
    <div
      className={cn(
        'flex items-center h-9',
        'hover:bg-muted/50 rounded-md transition-colors',
      )}
    >
      <TreeGuide isFirst={isFirst} isLast={isLast} />
      <div className="flex items-center gap-2 flex-1 min-w-0 pl-1.5">
        <Checkbox
          checked={layer.isVisible}
          onCheckedChange={(value) => {
            toggleLayerVisibility(layer.layer, value as boolean);
          }}
          className="size-4"
        />
        <div data-color-swatch>
          <ColorSwatch color={displayColor} onChange={handleColorChange} />
        </div>
        <LayerIcon className="size-4 flex-shrink-0" />
        <span className="text-sm truncate min-w-0 text-left">{layer.name}</span>
      </div>
      <div className="flex space-x-1 flex-shrink-0">
        <LayerRow.ZoomButton
          onClick={(e) => {
            e.stopPropagation();
            onFlyTo(layer);
          }}
        />
      </div>
    </div>
  );
};
